import React from "react";
import { ChevronDown, LogOut, Moon, Pencil, Settings, Sun } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useEffect, useState } from "react";
import {
  useAuthenticationContext,
  useCurrentWorkspace,
} from "@/store/authContext/authContext";
import { setTheme as setThemeAction, ThemeMode } from "@/store/features/theme";
import { useDispatch, useSelector } from "react-redux";
import { selectTheme } from "@/store/selectors/theme";
import { AppDispatch } from "@/store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PORTAL_URL } from "@/constants/api";
import type { ParitaWorkspace } from "@/common/user";

const SidebarAccountSection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentTheme = useSelector(selectTheme);
  const { state } = useAuthenticationContext();
  const { userData: user, handleLogout } = state;

  if (!user) {
    throw new Error("User data is not available");
  }

  const [theme, setTheme] = useState(() => {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches &&
      !currentTheme
    ) {
      return "dark";
    }
    return currentTheme || "light";
  });
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    dispatch(setThemeAction(theme as ThemeMode));
  }, [theme, dispatch]);

  const handleWorkspaceChange = async (value: string) => {
    // Workspace change is handled on the portal side
    window.location.href = `${PORTAL_URL}workspace/select?workspace=${value}`;
  };

  // Get current workspace
  const currentWorkspace = useCurrentWorkspace();
  const isCurrentWorkspaceAdmin = currentWorkspace?.is_admin || false;
  const companyLogo = currentWorkspace?.company_logo;
  const orgName = currentWorkspace?.name || "Settings";

  return (
    <div className="mt-auto pt-4">
      <Collapsible open={isAccountMenuOpen} onOpenChange={setIsAccountMenuOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-2 rounded-md transition-colors hover:bg-transparent dark:hover:bg-transparent">
            <div className="flex flex-col items-center justify-center w-full">
              <div className="flex flex-col items-center justify-center w-[100px] h-[100px] p-4 border border-primary rounded-xl">
                {companyLogo?.trim() && (
                  <img src={companyLogo} alt="Company Logo" className="" />
                )}
                <div className="text-sm italic">
                  {companyLogo?.trim() ? (
                    orgName
                  ) : (
                    <span className="font-bold">{orgName}</span>
                  )}
                </div>
              </div>
              <ChevronDown
                size={16}
                className={`text-sky-500 mt-1 transition-transform ${
                  isAccountMenuOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="bg-sky-100 rounded-lg">
          <div className="py-1">
            {user.workspaces.length > 1 && (
              <div className="px-8 py-4">
                <Select
                  value={currentWorkspace?.uuid}
                  onValueChange={handleWorkspaceChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {user.workspaces.map((workspace: ParitaWorkspace) => (
                      <SelectItem key={workspace.uuid} value={workspace.uuid}>
                        {workspace.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <button
              onClick={() => toggleTheme()}
              className="w-full text-left px-8 py-4 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors"
            >
              {theme === "dark" ? (
                <Sun size={16} className="mr-2" />
              ) : (
                <Moon size={16} className="mr-2" />
              )}
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </button>
            <a
              href={`${PORTAL_URL}user/me`}
              className="block w-full text-left px-8 py-4 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors"
            >
              <Pencil size={16} className="mr-2" />
              Edit Profile
            </a>
            {isCurrentWorkspaceAdmin && (
              <a
                href={`${PORTAL_URL}workspace/admin/users`}
                className="block w-full text-left px-8 py-4 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors"
              >
                <Settings size={16} className="mr-2" />
                Admin
              </a>
            )}
            <button
              onClick={() => handleLogout()}
              className="w-full text-left px-8 py-4 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors"
            >
              <LogOut size={16} className="mr-2" />
              Log out
            </button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default SidebarAccountSection;
